import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Sales Orders v3 — read-only browse over the OMS v3 relational draft tables
 * (`ar_draft_sales_orders` + `ar_draft_sales_order_line_items`). Powers the
 * admin-only Sales Orders v3 pages. Distinct from the `draftSalesOrders` group
 * (per-line/header editing) — this group is list + read-only detail.
 */

const UUIDSchema = z.string().uuid();
// DECIMAL columns come back from Sequelize as strings; accept either on the wire.
const NumericSchema = z.union([z.string(), z.number()]);

/**
 * Coarse v3-native lifecycle position, rolled up from the draft's status /
 * salesOrderId / approval decisions (see `rollupLifecycleStage` in
 * `@erp/shared-types`). Drives the list stage badge + the review-page stepper.
 */
// Keep in sync with `OrderLifecycleStage` in `@erp/shared-types`.
const LifecycleStageSchema = z.enum([
  "draft",
  "creating_discounts",
  "approving",
  "ready",
  "created",
  "rejected",
]);

const V3LineItemSchema = z
  .object({
    id: UUIDSchema,
    lineNumber: z.number().int(),
    customerIdentifier: z.string().nullable().optional(),
    customerDescription: z.string().nullable().optional(),
    quantity: NumericSchema.nullable().optional(),
    unitPrice: NumericSchema.nullable().optional(),
    amount: NumericSchema.nullable().optional(),
    unit: z.string().nullable().optional(),
    warehouse: z.string().nullable().optional(),
    inventoryStatus: z.string().nullable().optional(),
    listPrice: NumericSchema.nullable().optional(),
    listPriceMismatch: z.boolean().nullable().optional(),
    // The matched product variant — needed to scope a bulk discount to the line.
    selectedProductVariantId: UUIDSchema.nullable().optional(),
    additionalData: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .strip();

/** Read-only detail of a draft sales order (header + lines). */
const V3DetailSchema = z
  .object({
    id: UUIDSchema,
    organizationId: UUIDSchema,
    customerId: UUIDSchema.nullable().optional(),
    shipToLocationId: UUIDSchema.nullable().optional(),
    billToLocationId: UUIDSchema.nullable().optional(),
    purchaseOrderNumber: z.string().nullable().optional(),
    status: z.string(),
    source: z.string().nullable().optional(),
    orderDate: z.string().nullable().optional(),
    receivedDate: z.string().nullable().optional(),
    shipDate: z.string().nullable().optional(),
    deliveryDate: z.string().nullable().optional(),
    createdAt: z.string().nullable().optional(),
    updatedAt: z.string().nullable().optional(),
    totalAmount: NumericSchema.nullable().optional(),
    currency: z.string().nullable().optional(),
    salesOrderId: UUIDSchema.nullable().optional(),
    /** Rolled-up lifecycle position for the review-page stepper. */
    lifecycleStage: LifecycleStageSchema,
    /**
     * Shared split-group key (null unless this draft is part of a split) and the
     * number of drafts in that group. Drive the header "Split ×N" badge; show it
     * only when `splitGroupId != null && splitGroupSize > 1`.
     */
    splitGroupId: UUIDSchema.nullable(),
    splitGroupSize: z.int().nonnegative(),
    /** Resolved header values (rawData.computedData) keyed by field id. */
    computedData: z.record(z.string(), z.unknown()).nullable().optional(),
    lineItems: z.array(V3LineItemSchema),
  })
  .strip();

/** One member of a split group, for the review-page sibling list. */
const SplitGroupMemberSchema = z.object({
  id: UUIDSchema,
  purchaseOrderNumber: z.string().nullable(),
  status: z.string(),
  lifecycleStage: LifecycleStageSchema,
  totalAmount: NumericSchema.nullable(),
  currency: z.string().nullable(),
  lineItemCount: z.int().nonnegative(),
  /** The split rule that produced this member (null for the original draft). */
  splitRule: z.string().nullable(),
  /** True for the draft the request was made from. */
  isCurrent: z.boolean(),
});

/** Row shape for the v3 list table. */
const V3ListItemSchema = z.object({
  id: UUIDSchema,
  customerId: UUIDSchema.nullable(),
  customerName: z.string().nullable(),
  purchaseOrderNumber: z.string().nullable(),
  status: z.string(),
  /** Rolled-up lifecycle position for the list stage badge. */
  lifecycleStage: LifecycleStageSchema,
  source: z.string().nullable(),
  receivedDate: z.string().nullable(),
  orderDate: z.string().nullable(),
  totalAmount: NumericSchema.nullable(),
  currency: z.string().nullable(),
  lineItemCount: z.int().nonnegative(),
  shipToLocationName: z.string().nullable(),
  originId: z.string().nullable(),
  salesOrderOriginId: z.string().nullable(),
  /** Set when this draft belongs to a split — drives the list split indicator. */
  splitGroupId: UUIDSchema.nullable(),
});

/** Resolved review-page presentation config (metrics / columns / header fields). */
const SalesOrderUiConfigSchema = z.object({
  metrics: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      tooltip: z.string(),
      format: z.enum([
        "currency",
        "percent",
        "integer",
        "number",
        "date",
        "string",
      ]),
      requiresCost: z.boolean(),
      // Custom (org-added) metrics carry an aggregation + a line-field path or a
      // two-field compute that the frontend aggregates across the order's lines;
      // catalog metrics omit these.
      aggregation: z.enum(["count", "sum", "average"]).optional(),
      dataPath: z.string().optional(),
      compute: z
        .object({
          left: z.string(),
          op: z.enum(["subtract", "add", "multiply", "divide", "percent_off"]),
          right: z.string(),
        })
        .optional(),
      decimals: z.number().int().min(0).max(10).optional(),
    }),
  ),
  lineColumns: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      width: z.string().nullable(),
      alignRight: z.boolean(),
      // Custom (org-added) columns carry a dot-path into the line item +
      // optional format; built-in catalog columns omit both.
      dataPath: z.string().optional(),
      format: z.string().optional(),
      // Computed custom column: two operand dot-paths + an operator
      // (percent_off = (right − left) / right × 100).
      compute: z
        .object({
          left: z.string(),
          op: z.enum(["subtract", "add", "multiply", "divide", "percent_off"]),
          right: z.string(),
        })
        .optional(),
      decimals: z.number().int().min(0).max(10).optional(),
      // Help text shown as a tooltip on the column header.
      tooltip: z.string().optional(),
    }),
  ),
  headerFields: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      dataPath: z.string().optional(),
      control: z.string().optional(),
      format: z.string().optional(),
      editable: z.boolean().optional(),
      // No upper bound: widening the prior max (4) tripped the OpenAPI
      // breaking-change check; the value is an internal grid hint (1=¼ … 5=⅓).
      colSpan: z.number().int().min(1).optional(),
      // Selectable options for `control: "select"` fields (e.g. shipping method /
      // freight terms from the org's core enums). Empty/absent → rendered as text.
      options: z
        .array(z.object({ value: z.string(), label: z.string() }))
        .optional(),
      // Help text shown as a tooltip next to the field label.
      tooltip: z.string().optional(),
    }),
  ),
});

/**
 * One discovered, resolvable dot-path from sampling recent orders — surfaced in
 * the admin editor's data-path picker. `example` is a stringified, truncated
 * sample value; `coverage` is how many sampled records (orders for header paths,
 * lines for line paths) actually had the path populated.
 */
const FieldPathInfoSchema = z.object({
  path: z.string(),
  example: z.string().nullable(),
  type: z.string(),
  coverage: z.int().nonnegative(),
  sampled: z.int().nonnegative(),
});

const SampleFieldPathsInputSchema = z.object({
  limit: z.int().min(1).max(50).optional(),
});

const SampleFieldPathsResponseSchema = z.object({
  header: z.array(FieldPathInfoSchema),
  lineItem: z.array(FieldPathInfoSchema),
  sampledOrders: z.int().nonnegative(),
});

const V3PaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

const ListInputSchema = z.object({
  // Loose strings (not enums) so any value from the shared filters bar passes;
  // unsupported values simply match nothing.
  status: z.array(z.string()).optional(),
  // Rolled-up lifecycle stage(s) — what the list's Stage column shows. Filtered
  // server-side by mirroring `rollupLifecycleStage`. Loose strings; unknown
  // values match nothing. Values: draft | creating_discounts | approving |
  // ready | created | rejected.
  stage: z.array(z.string()).optional(),
  source: z.array(z.string()).optional(),
  customerId: z.array(UUIDSchema).optional(),
  search: z.string().optional(),
  sortBy: z
    .enum(["receivedDate", "orderDate", "totalAmount", "status"])
    .optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

const ListResponseSchema = z.object({
  draftSalesOrders: z.array(V3ListItemSchema),
  pagination: V3PaginationSchema,
});

export const salesOrdersV3Contracts = {
  list: oc
    .input(ListInputSchema)
    .output(ListResponseSchema)
    .route({ method: "GET" }),
  get: oc
    .input(z.object({ id: UUIDSchema }))
    .output(
      z.object({
        draftSalesOrder: V3DetailSchema,
        uiConfig: SalesOrderUiConfigSchema,
      }),
    )
    .route({ method: "GET" }),
  // All members of the draft's split group (the original + every child), for the
  // review-page sibling list. Empty when the draft was never split.
  getSplitGroup: oc
    .input(z.object({ id: UUIDSchema }))
    .output(z.object({ members: z.array(SplitGroupMemberSchema) }))
    .route({ method: "GET" }),
  // Sample recent orders for the org and return the resolvable dot-paths (header
  // + line item, incl. dynamic additionalData.* keys) with example values — the
  // data-path picker in the review-page config editor. Read-gated like get/list.
  sampleFieldPaths: oc
    .input(SampleFieldPathsInputSchema)
    .output(SampleFieldPathsResponseSchema)
    .route({ method: "GET" }),
  // Re-home a draft to another organization (OMS v3 routing). Gated by the
  // Execute:SalesOrderDraft permission. Reuses the reassignDraftSalesOrder service.
  reassign: oc
    .input(
      z.object({
        id: UUIDSchema,
        toOrganizationId: UUIDSchema,
        reason: z.string().trim().max(500).optional(),
      }),
    )
    .output(
      z.object({
        draftSalesOrderId: UUIDSchema,
        fromOrganizationId: UUIDSchema,
        toOrganizationId: UUIDSchema,
      }),
    )
    .route({ method: "POST" }),
  // Reject a draft sales order (sets status REJECTED + logs an activity entry).
  // Gated by Approve:SalesOrderDraftV3 — rejecting is the negative of approving.
  reject: oc
    .input(
      z.object({
        id: UUIDSchema,
        reason: z.string().trim().max(500).optional(),
      }),
    )
    .output(z.object({ id: UUIDSchema, status: z.string() }))
    .route({ method: "POST" }),
  // Soft-delete a draft sales order. Gated by Delete:SalesOrderDraftV3.
  delete: oc
    .input(z.object({ id: UUIDSchema }))
    .output(z.object({ id: UUIDSchema, deleted: z.literal(true) }))
    .route({ method: "POST" }),
} as const;

export type SalesOrderFieldPathInfo = z.infer<typeof FieldPathInfoSchema>;
export type SampleFieldPathsResponse = z.infer<
  typeof SampleFieldPathsResponseSchema
>;
export type SalesOrderV3ListItem = z.infer<typeof V3ListItemSchema>;
export type SalesOrderV3Detail = z.infer<typeof V3DetailSchema>;
export type SalesOrderV3SplitGroupMember = z.infer<
  typeof SplitGroupMemberSchema
>;
export type ListSalesOrdersV3Input = z.infer<typeof ListInputSchema>;
export type ListSalesOrdersV3Response = z.infer<typeof ListResponseSchema>;
